// src/pages/auth/Login.tsx
import React, { useEffect, useState } from "react";
import { Box, TextField, Typography, Paper } from "@mui/material";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Logo from "../../assets/AuthLogo.svg";
import AuthBg from "../../assets/AuthBg4.svg";
import BasicDialog from "../../components/BasicDialog";
import EmailVarification from "./EmailVarification";
import { userLogin } from "../../store/actions/authActions";
import LoadButton from "../../components/LoadButton";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { auth } = useSelector((state) => state.auth);

  const [openForget, setForget] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (auth) navigate("/dashboard");
  }, [auth, navigate]);

  const handleForgetPassword = () => setForget(true);
  const forgetpasswordClose = () => setForget(false);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
      //  background: "linear-gradient(135deg,#456B8F, #33658D, #004579)",
      bgcolor:'#fff'
      }}
    >
      {/* LEFT SIDE BG */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "block" }, // hide on mobile
          backgroundImage: `url(${AuthBg})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* RIGHT SIDE FORM */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 2, md: 4 },
        }}
      >
        <Paper
          elevation={6}
          sx={{
            width: "100%",
            maxWidth: 400,
            borderRadius: 4,
            p: { xs: 3, sm: 4 },
            textAlign: "center",
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={Logo}
            alt="Logo"
            sx={{
              height: 80,
              mb: 2,
              "@media(max-width: 600px)": { height: 50 },
            }}
          />

          {/* Heading */}
          <Typography
            variant="h5"
            fontWeight="bold"
            color="primary"
            gutterBottom
          >
            Welcome Back 👋
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Login to continue to your dashboard
          </Typography>

          {/* Form */}
          <Formik
            initialValues={{ email: "", password: "" }}
            validate={(values) => {
              const errors = {};
              if (!values.email) {
                errors.email = "Email is Required";
              } else if (
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
              ) {
                errors.email = "Invalid email address";
              }
              if (!values.password) errors.password = "Password is Required";
              return errors;
            }}
            onSubmit={async (values, { setSubmitting }) => {
              setLoading(true);
              const { type } = await dispatch(userLogin(values));
              setLoading(false);
              if (type === "user/login/fulfilled") {
                const url = sessionStorage.getItem("requestUrl");
                navigate(url ? url : "/dashboard");
                sessionStorage.removeItem("requestUrl");
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              handleBlur,
            }) => (
              <form
                onSubmit={handleSubmit}
                style={{ display: "flex", flexDirection: "column", gap: 20 }}
              >
                {/* Email */}
                <TextField
                  type="email"
                  name="email"
                  size="small"
                  placeholder="Email Address"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&.Mui-focused fieldset": {
                        borderColor: "#6a11cb",
                        boxShadow: "0 0 0 2px rgba(106, 17, 203, 0.2)",
                      },
                    },
                  }}
                />
                {errors.email && touched.email && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ textAlign: "left", mt: -1 }}
                  >
                    {errors.email}
                  </Typography>
                )}

                {/* Password */}
                <TextField
                  type="password"
                  name="password"
                  size="small"
                  placeholder="Password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&.Mui-focused fieldset": {
                        borderColor: "#2575fc",
                        boxShadow: "0 0 0 2px rgba(37, 117, 252, 0.2)",
                      },
                    },
                  }}
                />
                {errors.password && touched.password && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ textAlign: "left", mt: -1 }}
                  >
                    {errors.password}
                  </Typography>
                )}

                {/* Forget Password */}
                <Typography
                  onClick={handleForgetPassword}
                  sx={{
                    color: "#2575fc",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                    textAlign: "right",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Forget Password?
                </Typography>

                {/* Button */}
                <LoadButton
                  loading={loading}
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: "bold",
                    py: 1.2,
                    background: "linear-gradient(90deg,#6a11cb,#2575fc)",
                    "&:hover": {
                      background: "linear-gradient(90deg,#5a0dbd,#1b63db)",
                    },
                  }}
                >
                  Login
                </LoadButton>
              </form>
            )}
          </Formik>
        </Paper>
      </Box>

      {/* Forget Password Dialog */}
      <BasicDialog open={openForget} handleClose={forgetpasswordClose}>
        <EmailVarification handleClose={forgetpasswordClose} />
      </BasicDialog>
    </Box>
  );
};

export default Login;
